import React, { useState, useEffect } from 'react';
import {
    Paper,
    Button,
    FormControl,
    Select,
    MenuItem,
    Stack,
    Typography,
    Box,
    Divider,
    CardMedia,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setCountOnPage, setCity, resetFilters } from '../../store/slices/filtersSlice';
import { useGetCitiesQuery } from '../../store/api/goodsApi';
import { themeColors, semanticColors } from '../../theme/colors';
import GearLoader from '../Common/GearLoader';

const BannerComponent: React.FC = () => {
    const [currentBanner, setCurrentBanner] = useState(0);

    const banners = [
        {
            href: 'https://skynet-service.ru/products?product=kevlar-covers',
            src: 'https://price.skynet-service.com/image/ads/ads1.png',
            alt: 'Kevlar чехлы'
        },
        {
            href: 'https://skynet-service.ru/products?product=protective-glass&q=%D1%81%D1%82%D0%B5%D0%BA%D0%BB%D0%B0+SKY',
            src: 'https://price.skynet-service.com/image/ads/ads2.png',
            alt: 'Защитные стекла SKY'
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentBanner((prev) => (prev + 1) % banners.length);
        }, 6000);

        return () => clearInterval(interval);
    }, [banners.length]);

    return (
        <Box sx={{
            borderRadius: 1.5,
            mt: 2,
            overflow: 'hidden',
            position: 'relative',
            minHeight: "25vw",
            width: '100%',
        }}>
            <a
                href={banners[currentBanner].href}
                target='_blank'
                rel="noreferrer"
                style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer'
                }}
            >
                <CardMedia
                    component="img"
                    image={banners[currentBanner].src}
                    alt={banners[currentBanner].alt}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '12px',
                    }}
                    loading="lazy"
                />
            </a>

            <Box sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                display: 'flex',
                gap: 0.5
            }}>
                {banners.map((_, index) => (
                    <Box
                        key={index}
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: currentBanner === index
                                ? 'rgba(255, 255, 255, 0.9)'
                                : 'rgba(255, 255, 255, 0.4)',
                            cursor: 'pointer'
                        }}
                        onClick={() => setCurrentBanner(index)}
                    />
                ))}
            </Box>
        </Box>
    );
};

const FiltersPanel: React.FC = () => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector((state) => state.filters);
    const { data: cities = [], isLoading: citiesLoading, error: citiesError } = useGetCitiesQuery();

    const [pendingFilters, setPendingFilters] = useState({
        city: filters.city,
        countOnPage: filters.countOnPage,
    });

    useEffect(() => {
        setPendingFilters({
            city: filters.city,
            countOnPage: filters.countOnPage,
        });
    }, [filters.city, filters.countOnPage]);

    const handleApplyFilters = () => {
        if (pendingFilters.city !== filters.city) {
            dispatch(setCity(pendingFilters.city));
        }
        if (pendingFilters.countOnPage !== filters.countOnPage) {
            dispatch(setCountOnPage(pendingFilters.countOnPage));
        }
    };

    const handleClear = () => {
        setPendingFilters({ city: 0, countOnPage: 100 });
        dispatch(resetFilters());
    };

    const hasChanges = pendingFilters.city !== filters.city ||
        pendingFilters.countOnPage !== filters.countOnPage;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                backgroundColor: semanticColors.glass.background,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${semanticColors.border.light}`,
                minHeight: { md: 'calc(100vh - 160px)' },
                position: 'sticky',
                top: 80,
            }}
        >
            <Typography
                variant="h6"
                gutterBottom
                sx={{
                    color: semanticColors.text.primary,
                    fontWeight: 600,
                    mb: 3,
                    fontSize: '1.1rem',
                }}
            >
                Дополнительные фильтры
            </Typography>

            <Stack spacing={3}>
                {/* Город */}
                <Box>
                    <Typography variant="caption" sx={{
                        mb: 1,
                        display: 'block',
                        color: semanticColors.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.7rem',
                    }}>
                        Город {citiesLoading && <GearLoader size={14} />}
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={pendingFilters.city}
                            onChange={(e) => setPendingFilters(prev => ({ ...prev, city: e.target.value as number }))}
                            disabled={citiesLoading || !!citiesError}
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: semanticColors.border.light,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: themeColors['300'],
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: themeColors['400'],
                                    borderWidth: 1,
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                },
                            }}
                        >
                            <MenuItem value={0}>Все города</MenuItem>
                            {cities.map((city) => (
                                <MenuItem key={city.id} value={city.id}>
                                    {city.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ borderColor: semanticColors.border.light }} />

                {/* Количество на странице */}
                <Box>
                    <Typography variant="caption" sx={{
                        mb: 1,
                        display: 'block',
                        color: semanticColors.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.7rem',
                    }}>
                        Показывать по
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={pendingFilters.countOnPage}
                            onChange={(e) => setPendingFilters(prev => ({ ...prev, countOnPage: e.target.value as number }))}
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: semanticColors.border.light,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: themeColors['300'],
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: themeColors['400'],
                                    borderWidth: 1,
                                },
                            }}
                        >
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={500}>500</MenuItem>
                            <MenuItem value={1000}>1000</MenuItem>
                            <MenuItem value={1500}>1500</MenuItem>
                            <MenuItem value={2000}>2000</MenuItem>
                            <MenuItem value={2500}>2500</MenuItem>
                            <MenuItem value={5000}>5000</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ borderColor: semanticColors.border.light }} />

                {/* Кнопки */}
                <Stack spacing={2}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleApplyFilters}
                        disabled={!hasChanges}
                        sx={{
                            backgroundColor: themeColors['500'],
                            color: 'white',
                            py: 1.2,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: themeColors['600'],
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                color: 'rgba(0, 0, 0, 0.26)',
                            },
                        }}
                    >
                        Применить фильтры
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleClear}
                        sx={{
                            color: themeColors['600'],
                            borderColor: themeColors['300'],
                            py: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                borderColor: themeColors['400'],
                            },
                        }}
                    >
                        Очистить фильтры
                    </Button>
                </Stack>
            </Stack>

            <BannerComponent />
        </Paper>
    );
};

export default FiltersPanel;